import React, { ChangeEvent } from 'react'
import styled from 'styled-components'
import { Colors } from '../../constants'
import { Settings, useSettings } from '../../providers'
import { Modal } from '../Modal'

interface Props {
  onClose: () => void
}

export function SettingsModal({ onClose }: Props) {
  const { settings, setSettings, restoreDefaults } = useSettings()

  function onChange(name: keyof Settings, value: number | boolean) {
    setSettings({ ...settings, [name]: value })
  }

  function onSubmitDeadlineChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('submitDeadline', e.target.valueAsNumber)
  }

  function onExecutionDeadlineChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('executionDeadline', e.target.valueAsNumber)
  }

  function onGasPriceMultiplierChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('gasPriceMultiplier', e.target.valueAsNumber)
  }

  function onSlippageToleranceChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('slippageTolerance', e.target.valueAsNumber)
  }

  function onGasLimitOverpayChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('gasLimitOverpay', e.target.valueAsNumber)
  }

  function onGasPriceSpeedChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('gasPriceSpeed', e.target.valueAsNumber)
  }

  function onUseWethChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('useWeth', !!e.target.valueAsNumber)
  }

  function onUseSuggestedDeposit(e: ChangeEvent<HTMLInputElement>) {
    onChange('useSuggestedDeposit', !!e.target.valueAsNumber)
  }

  function onMinRatioChange(e: ChangeEvent<HTMLInputElement>) {
    onChange('minRatioChange', e.target.valueAsNumber)
  }

  function onDepositSlippage(e: ChangeEvent<HTMLInputElement>) {
    onChange('depositSlippageInBasisPoints', e.target.valueAsNumber)
  }

  function onRestoreDefaults() {
    restoreDefaults()
  }

  return (
    <Modal title="Advanced Settings" onClose={onClose}>
      <Wrapper>
        <Field>
          <Label htmlFor="submitDeadline">Submit Deadline</Label>
          <Value>{toHumanTime(settings.submitDeadline)}</Value>
          <Description>If your transaction doesn't get mined faster than this it will revert.</Description>
          <Input
            type="range"
            value={settings.submitDeadline}
            min={5 * 60}
            max={24 * 60 * 60}
            step={5 * 60}
            onChange={onSubmitDeadlineChange}
          />
        </Field>
        <Field>
          <Label htmlFor="executionDeadline">Execution Deadline</Label>
          <Value>{toHumanTime(settings.executionDeadline)}</Value>
          <Description>If your order doesn't get executed faster than this it will revert.</Description>
          <Input
            type="range"
            value={settings.executionDeadline}
            min={15 * 60}
            max={24 * 60 * 60}
            step={5 * 60}
            onChange={onExecutionDeadlineChange}
          />
        </Field>
        <Field>
          <Label htmlFor="gasPriceMultiplier">Gas Price Multiplier</Label>
          <Value>{settings.gasPriceMultiplier / 1000}</Value>
          <Description>
            Used for prepaying for the execution to account for gas price fluctuation. Higher values increase the chance
            of the transaction succeeding as well as the transaction fee.
          </Description>
          <Input
            type="range"
            value={settings.gasPriceMultiplier}
            min={1000}
            max={1200}
            step={5}
            onChange={onGasPriceMultiplierChange}
          />
        </Field>
        <Field>
          <Label htmlFor="gasLimitOverpay">Gas Limit Overpay</Label>
          <Value>{settings.gasLimitOverpay}</Value>
          <Description>
            Used for prepaying for the execution. If you are doing something fancy with your transaction execution can
            use more gas.
          </Description>
          <Input
            type="range"
            value={settings.gasLimitOverpay}
            min={0}
            max={100_000}
            step={10_000}
            onChange={onGasLimitOverpayChange}
          />
        </Field>
        <Field>
          <Label htmlFor="gasPriceSpeed">Gas Price Speed</Label>
          <Value>{['slow', 'average', 'fast'][settings.gasPriceSpeed]}</Value>
          <Description>
            Default gas price. We recommend leaving this set to 'fast' to avoid stuck transactions.
          </Description>
          <Input
            type="range"
            value={settings.gasPriceSpeed}
            min={0}
            max={2}
            step={1}
            onChange={onGasPriceSpeedChange}
          />
        </Field>
        <Field>
          <Label htmlFor="slippageTolerance">Slippage Tolerance</Label>
          <Value>{settings.slippageTolerance / 10}%</Value>
          <Description>If the price changes more than this percentage, your order will be reverted.</Description>
          <Input
            type="range"
            value={settings.slippageTolerance}
            min={1}
            max={100}
            step={1}
            onChange={onSlippageToleranceChange}
          />
        </Field>
        <Field>
          <Label htmlFor="useWeth">Use WETH</Label>
          <Value>{settings.useWeth ? 'true' : 'false'}</Value>
          <Description>
            If this option is set to false, we wrap your ETH, but you can chose to use your own WETH.
          </Description>
          <Input type="range" value={settings.useWeth ? 1 : 0} min={0} max={1} step={1} onChange={onUseWethChange} />
        </Field>
        <Field>
          <Label htmlFor="useSuggestedDeposit">Use suggested deposit</Label>
          <Value>{settings.useSuggestedDeposit ? 'true' : 'false'}</Value>
          <Description>If this option is set to false, you can deposit any amounts to reserves.</Description>
          <Input
            type="range"
            value={settings.useSuggestedDeposit ? 1 : 0}
            min={0}
            max={1}
            step={1}
            onChange={onUseSuggestedDeposit}
          />
        </Field>
        <Field>
          <Label htmlFor="minRatioChange">Minimum Ratio Change to Swap</Label>
          <Value>{settings.minRatioChange} %</Value>
          <Description>
            If the ratio of reserves changes by more than this percentage, an automatic swap will be done to maximize
            amount of deposited tokens.
            <br />
            NOTE: To deposit unproportionally this should be left at 0.
          </Description>
          <Input type="range" value={settings.minRatioChange} min={0} max={500} step={25} onChange={onMinRatioChange} />
        </Field>
        <Field>
          <Label htmlFor="depositSlippageInBasisPoints">Deposit Slippage</Label>
          <Value>{settings.depositSlippageInBasisPoints / 100} %</Value>
          <Description>
            If the price changes more than the deposit slippage, the transaction will be reverted.
          </Description>
          <Input
            type="range"
            value={settings.depositSlippageInBasisPoints}
            min={0}
            max={2000}
            step={10}
            onChange={onDepositSlippage}
          />
        </Field>
        <Field>
          <Button onClick={onRestoreDefaults}>Restore Default Settings</Button>
        </Field>
      </Wrapper>
    </Modal>
  )
}

const Wrapper = styled.div`
  padding: 20px;
`

const Field = styled.div``

const Label = styled.label`
  margin-right: 12px;
  display: inline-block;
  font-size: 14px;
`

const Value = styled.div`
  display: inline-block;
  font-weight: bold;
  font-size: 14px;
`

const Description = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${Colors.DarkerGray};
`

const Input = styled.input`
  margin-top: 5px;
  width: 100%;
`

const Button = styled.button`
  margin-top: 10px;
  display: block;
  width: 100%;
  font-size: 14px;
  text-decoration: underline;
  background: none;
  border: 0;
  text-align: center;

  &:hover {
    color: ${Colors.Hover};
  }
`

function toHumanTime(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const secondsLeft = seconds % 60
  if (secondsLeft !== 0) {
    return `${minutes}m ${secondsLeft}s`
  }
  return `${minutes}m`
}
